import { useState, useEffect } from 'react';
import type { User } from 'firebase/auth';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getJwtToken, hasAuthTokenInUrl, isJwtTokenValid, authenticateWithJwt, clearJwtToken, setStoredAuthToken } from '../services/jwtService';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const jwtAuthInProgressRef = useState(() => ({ current: hasAuthTokenInUrl() }))[0];

  useEffect(() => {
    console.log('[AUTH DEBUG] Initialisation du hook useAuth');
    
    // Vérifier si un token JWT est présent dans l'URL (inter-apps)
    const tokenInUrl = new URLSearchParams(window.location.search).get('auth_token');
    console.log('[AUTH DEBUG] Token dans URL:', tokenInUrl ? 'Présent' : 'Absent');
    
    // Configurer l'écouteur d'état d'authentification Firebase
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (jwtAuthInProgressRef.current) {
        return;
      }
      console.log('[AUTH DEBUG] Changement d\'état d\'authentification Firebase:', user ? 'Utilisateur connecté' : 'Utilisateur déconnecté');
      if (user) {
        console.log('[AUTH DEBUG] ID utilisateur:', user.uid);
        console.log('[AUTH DEBUG] Email utilisateur:', user.email);

        // Mettre à jour l'activité sans bloquer l'app (best effort)
        setDoc(
          doc(db, 'users', user.uid),
          { lastActiveAt: serverTimestamp() },
          { merge: true }
        ).catch((e) => {
          console.error('[AUTH DEBUG] Échec mise à jour lastActiveAt:', e);
        });

        // Garantir le rôle admin pour l'email admin (best effort)
        if (user.email === 'jpigra@gmail.com') {
          setDoc(
            doc(db, 'users', user.uid),
            { role: 'admin' },
            { merge: true }
          ).catch((e) => {
            console.error('[AUTH DEBUG] Échec mise à jour role=admin:', e);
          });
        }
      }
      
      setUser(user);
      setStatus(user ? 'authenticated' : 'unauthenticated');
      setError(null);
    });
    
    // Vérifier si nous avons un token JWT valide
    const checkJwtAuthentication = async () => {
      // Si le token est dans l'URL, le service JWT le récupèrera et le stockera
      const token = getJwtToken();
      
      if (token && !auth.currentUser) {
        console.log('[AUTH DEBUG] Token JWT disponible, vérification de sa validité');
        
        jwtAuthInProgressRef.current = true;
        setStatus('loading');

        const isValid = await isJwtTokenValid();
        if (isValid) {
          console.log('[AUTH DEBUG] Token JWT valide, tentative d\'authentification');
          
          try {
            // Tenter l'authentification avec le token JWT
            const success = await authenticateWithJwt();
            
            if (success) {
              console.log('[AUTH DEBUG] Authentification JWT réussie');
              // L'utilisateur est maintenant authentifié via JWT
              // Le changement d'état sera géré par l'observateur onAuthStateChanged
            } else {
              console.log('[AUTH DEBUG] Échec de l\'authentification JWT');
            }
          } catch (error) {
            console.error('[AUTH DEBUG] Erreur lors de l\'authentification JWT:', error);
          }
        } else {
          console.log('[AUTH DEBUG] Token JWT expiré ou invalide');
          // Supprimer le token expiré
          clearJwtToken();
        }

        jwtAuthInProgressRef.current = false;

        const nextUser = auth.currentUser;
        setUser(nextUser);
        setStatus(nextUser ? 'authenticated' : 'unauthenticated');
      }
    };
    
    // Exécuter la vérification JWT
    checkJwtAuthentication();

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Alimenter les stats admin: dernière connexion
      await setDoc(
        doc(db, 'users', userCredential.user.uid),
        { lastLogin: serverTimestamp() },
        { merge: true }
      );
      return userCredential.user;
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Alimenter les stats admin: première connexion
      await setDoc(
        doc(db, 'users', userCredential.user.uid),
        { lastLogin: serverTimestamp() },
        { merge: true }
      );
      return userCredential.user;
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const signInWithToken = async (token: string) => {
    try {
      setError(null);
      console.log('[AUTH DEBUG] Tentative d\'authentification avec token:', token ? token.substring(0, 10) + '...' : 'aucun token');
      
      if (token) {
        setStoredAuthToken(token);
      }
      
      jwtAuthInProgressRef.current = true;
      setStatus('loading');

      const isValid = await isJwtTokenValid();
      if (!isValid) {
        throw new Error('Token d\'authentification invalide ou expiré');
      }

      const success = await authenticateWithJwt();
      if (!success) {
        throw new Error('Échec de l\'authentification JWT');
      }

      jwtAuthInProgressRef.current = false;

      setUser(auth.currentUser);
      setStatus(auth.currentUser ? 'authenticated' : 'unauthenticated');

      return auth.currentUser;
      
    } catch (error: any) {
      jwtAuthInProgressRef.current = false;
      setError(error.message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      clearJwtToken();
      await firebaseSignOut(auth);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  return {
    user,
    status,
    error,
    signIn,
    signUp,
    signInWithToken,
    signOut,
    isAuthenticated: status === 'authenticated',
    isLoading: status === 'loading',
  };
};

export default useAuth;
